import React, { useState, useMemo, useCallback } from 'react'

import { IKioskComponentProps } from './checkin'
import { CarrierSearchField } from 'browser/components/atomic-elements/molecules/fields/select-field/carrier-search-field'
import { Formatter } from 'shared-libs/helpers/formatter'
import { getDOTNumber } from 'browser/app/utils/utils'
import apis from 'browser/app/models/apis'
import { KioskNextButton, KioskInputField, navigateToKioskStory } from '../utils'

import { Entity } from 'shared-libs/models/entity'
import { RequestMethod } from 'shared-libs/models/store'
import { edgeFromEntity } from 'shared-libs/helpers/formulas/modules'
import _ from 'lodash'
import './_driver-checkin-details.scss'

declare let window: any

/**
 * Kiosk driver check-in details form to set/update driver name and carrier information
 */
const DriverCheckInDetails: React.FC<IKioskComponentProps> = ({ context, onNext }) => {
  const user: any = context?.user
  const firm: any = context?.firm

  const [firstName, setFirstName] = useState(user?.person?.firstName ?? '')
  const [lastName, setLastName] = useState(user?.person?.lastName ?? '')
  const [dotNumber, setDotNumber] = useState(getDOTNumber(firm) ?? '')
  const [carrier, setCarrier] = useState<Entity>(null)
  const [isLoading, setIsLoading] = useState(false)

  const isUserModified = useMemo(() => {
    const currFirstName = user?.person?.firstName ?? ''
    const currLastName = user?.person?.lastName ?? ''
    const currDotNumber = getDOTNumber(firm) ?? ''

    return firstName !== currFirstName || lastName !== currLastName || dotNumber !== currDotNumber
  }, [firstName, lastName, dotNumber])

  const handleNext = async (e?: React.FormEvent) => {
    e?.preventDefault()
    setIsLoading(true)

    try {
      const payload = {
        firstName,
        lastName,
        dotNumber,
        phoneNumber: context?.phoneNumber,
        storyId: context?.storyId,
        containerId: context?.containerId,
        carrierName: carrier?.get?.('legalName'),
      }

      // create or update user
      const { user, firm } = await createOrUpdateUser()

      // create checkin workflow
      const checkin = await createCheckinWorkflow({
        ...payload,
        user,
        firm,
      })

      await navigateToKioskStory(checkin, 'story_kiosk_checkin')

      setIsLoading(false)
    } catch (error) {
      console.error('Error during check-in submission:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCarrierChange = (value: string, option: any = {}) => {
    setDotNumber(value)
    setCarrier(option)
  }

  const createOrUpdateUser = async () => {
    let userEntity = user
    let firmEntity = firm
    const payload = {
      firstName,
      lastName,
      dotNumber,
      phoneNumber: context?.phoneNumber,
      carrierName: carrier?.get?.('legalName') ?? _.get(carrier, 'legalName'),
    }
    if (isUserModified) {
      // if the user is modified, then need to sync back to the server
      userEntity = await apis.resolveUser(payload)
      if (userEntity) {
        const firmId = userEntity.get('owner.firm.entityId')
        firmEntity = await apis.getStore().getOrFetchRecord(firmId)
      }
    }

    return {
      user: userEntity,
      firm: firmEntity,
    }
  }

  const createCheckinWorkflow = async (props: any): Promise<Entity> => {
    const { user, dotNumber, storyId, containerId } = props

    const payload = {
      userId: user.uniqueId,
      shareTo: {
        user: edgeFromEntity(user),
      },
      dotNumber: dotNumber,
      checkinStory: {
        name: 'Kiosk Checkin',
        storyId,
        containerId,
      },
      checkinProperties: [
        {
          path: 'core_yms_execution.driver.entityId',
          value: user.uniqueId,
        },
        {
          path: 'core_yms_execution.initiationType',
          value: 'Kiosk',
        },
        {
          path: 'core_yms_execution.initiator',
          value: 'Driver',
        },
      ],
    }

    const { formUri, userId, checkinId }: any = await apis.performCheckin(payload)

    const checkin = await apis.getStore().findRecord(checkinId)

    // save formUri back to the execution

    checkin.set('core_yms_execution.kiosk.driverMobileUri', formUri)
    await checkin.save({}, RequestMethod.PATCH)

    return checkin
  }

  const isNextDisabled = !firstName || !lastName || !dotNumber

  return (
    // <div className="kiosk-driver-details-container">
    <div className="kiosk-container">
      <div className="kiosk-main-content">
        <h1 className="kiosk-instructions-text">Confirm your name and Carrier DOT #</h1>

        <form className="kiosk-info-group" onSubmit={handleNext}>
          <KioskInputField
            id="phone"
            label="Phone"
            value={Formatter.formatPhoneNumber(context?.phoneNumber ?? '')}
          />

          <div className="kiosk-name-fields">
            <KioskInputField
              id="firstName"
              label="First Name"
              placeholder="First Name"
              value={firstName}
              onChange={setFirstName}
            />
            <KioskInputField
              id="lastName"
              label="Last Name"
              value={lastName}
              placeholder="Last Name"
              onChange={setLastName}
            />
          </div>

          <div className="kiosk-carrier-search-wrapper">
            <CarrierSearchField
              label="Carrier"
              placeholder="Search USDOT"
              showRemoveOption={false}
              isHorizontalLayout={false}
              clearable={true}
              value={dotNumber}
              onInputChange={(value, actionMeta) => {
                const isMenuClosedAction =
                  actionMeta?.action === 'input-blur' || actionMeta?.action === 'menu-close'
                if (isMenuClosedAction && !value) {
                  return
                }
                handleCarrierChange(value)
              }}
              onChange={handleCarrierChange}
            />
          </div>

          <KioskNextButton isDisabled={isNextDisabled} isLoading={isLoading} onClick={handleNext}>
            CONTINUE TO APPOINTMENT LOOKUP
          </KioskNextButton>
        </form>
      </div>
    </div>
  )
}

export default DriverCheckInDetails
